import { Badge } from "@bruntworkwear-apps/ui";
import type { AllocationResult, PoSummary, ProductRate } from "@/shared/api";

/** Tone and text for one reading of an order's rate. */
type RateBadge = { label: string; tone: "success" | "warning" | "danger" };

/** True when the order's products were invoiced at rates that differ. */
function spansProducts(products: ProductRate[]): boolean {
  const rates = new Set(
    products
      .map((entry) => entry.customs_rate)
      .filter((rate): rate is number => rate !== null)
      .map((rate) => rate.toFixed(6)),
  );
  return rates.size > 1;
}

/** Lists what each product on the order was actually invoiced at. */
function productSummary(products: ProductRate[]): string {
  return products
    .map((entry) => `${entry.product} ${entry.customs_rate?.toFixed(4) ?? "no rate"}`)
    .join("; ");
}

/** True when even one product's own rows disagreed on a rate. */
function anyProductAveraged(products: ProductRate[]): boolean {
  return products.some((entry) => entry.rate_source === "weighted_average");
}

/**
 * How the order's rate should read, before the exact-quantity fact is added.
 *
 * An order spanning several products is the case that used to be reported
 * wrongly. Its order-wide rate is a blend, but no line is costed at it - each
 * takes its own product's rate - so calling it "averaged" sends a reviewer
 * looking for an error that is not there.
 *
 * The two are not exclusive, though, and that is the trap: an order can split
 * by product *and* have one of those products blended across two rates. Then a
 * line really is costed at an average, so the warning has to survive the split
 * rather than be replaced by the reassuring badge.
 */
function rateBadge(allocation: AllocationResult): RateBadge {
  const products = allocation.product_rates;
  const averaged = anyProductAveraged(products);

  if (spansProducts(products)) {
    const label = `by product · ${products.length}`;
    return averaged
      ? { label: `${label} · averaged`, tone: "warning" }
      : { label, tone: "success" };
  }
  if (averaged || allocation.rate_source === "weighted_average") {
    return { label: "averaged", tone: "warning" };
  }
  return { label: "single rate", tone: "success" };
}

/**
 * Where the customs rate came from.
 *
 * Two separate facts, in one cell. The rate is either invoiced on a single
 * shipment, averaged across several, or kept per product - and separately, the
 * rows it came from either accounted for the receipt quantity exactly or were
 * filled oldest-first until the quantity ran out. An averaged rate over the
 * exactly-matching rows is a materially better number than an averaged rate over
 * whatever the date window happened to contain, so the two are not collapsed
 * into one badge.
 */
export function RateSourceCellView({ order }: { order: PoSummary }) {
  const allocation = order.allocation;

  if (allocation === null || allocation.rate_source === "unavailable") {
    return <Badge tone="danger">no rate</Badge>;
  }

  const { label, tone } = rateBadge(allocation);
  const perProduct = spansProducts(allocation.product_rates);

  // Independent of how the rate was reached. Most of the split orders on a real
  // close are exactly matched too, and suppressing the fact for them threw away
  // the strongest evidence the cell carries.
  const exact = allocation.quantity_aligned;

  const explanations = [
    perProduct
      ? `Each line is costed at its own product's rate: ${productSummary(allocation.product_rates)}`
      : null,
    exact ? "The receipt quantity matched tracker rows exactly" : null,
  ].filter((part): part is string => part !== null);
  const title = explanations.length > 0 ? explanations.join(". ") : undefined;

  return (
    <span title={title}>
      <Badge tone={tone}>{exact ? `${label} · exact qty` : label}</Badge>
    </span>
  );
}
